import { savedInventoryVitalityRows } from '../resources/resource-adjust';
import { readInventoryField, readInventoryRows } from './inventory-raw-readers';
import { encounterCreatureField, encounterField, layerOf } from '../../shared/array-types';
import { savedTurnResourceFrame } from '../resources/resource-adjust';
import { readCreatureStatuses } from './creature-status-reader';
import { phpJsonTruthy } from './php-json-values';
import { applyAppliedFrame } from '../transport/apply-applied-frame';
import { expandAppliedFrameInputs, type AppliedNewEntity, type AppliedFrame, type AppliedFrameResult, type AppliedTouchedRow } from '../transport/applied-frame';
import { actionBarsOnlyFrame, holdsSavedActionBars, type SavedRowCorrection } from '../transport/action-bar-saved-rows';
import { hydrateSpawnBirth } from '../spawn/birth-inputs';
import { applyBasePatch, baseInventoryIds } from './base-patch';
import { savedPayloadMemberFrame } from './saved-payload-member-delta';
import type { BaseBusy, BaseEnvelope, BasePatchFrame, BasePatchOp } from './base-patch';
import { savedEquipmentFrame, sameSavedInventoryColumn } from './saved-equipment-delta';
import { relocationArrivalIds, relocationDefinitionsUnchanged, savedCreatureRelocationFrame, type RelocationMemberBatch, type RelocationMemberFailure } from './saved-creature-relocation';
import type { FromBaseEntryResult } from './from-base-entry';
import { fromBaseClone, fromBaseRecord } from './from-base-context';
import { completePendingBirth } from './pending-birth-revision';
import { savedFloorInventoryFrame } from './saved-floor-inventory-delta';
import { compileFloorEnchantmentMount, floorEnchantmentMount } from './floor-enchantment-delta';
import { savedInventoryLinkFrame } from './saved-inventory-links';
import { readItemField } from './item-field-reader';
import { encounterMemberSharedInputs, encounterMemberInput } from './encounter-member-input';
import { savedEncounterShapeRows } from './encounter-shape-delta';
import { createEncumbranceRetirementRuntime } from './encumbrance-retirement-runtime';
import type { EncumbranceStatusAdd } from './encumbrance-retirement-runtime';
import { skipUnported } from '../unported';
import { savedInventoryBirthFrame } from './saved-inventory-birth-delta';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { sameTree } from './tree-transfer';
import { parsedAppliedFrame } from '../transport/applied-frame-plan';
import { savedChargeFrame } from './saved-charge-delta';
import type { SavedSkillGrantPatch } from './saved-skill-grant-delta';
import { savedSummonConditionPatch } from './saved-summon-condition-delta';
import { entityRuntimeMetadata } from './entity-runtime-metadata';
import { memberReadsSceneConditions } from './member-condition-context';
import { savedInventoryMoveFrame } from './saved-inventory-move-delta';
import { partyPlayerOps, savedPartyPlayerFrame } from './saved-party-player-delta';

function ownedBirthBase(base: BaseEnvelope['base'], held: BaseEnvelope['base']): BaseEnvelope['base'] {
    return hydrateSpawnBirth(fromBaseClone(base), held); // dnr-clone: allow size=one incoming birth's saved rows and required ids; why=detach new rows before borrowing held immutable sources
}
function initialBirthResult(newborn: AppliedNewEntity): FromBaseEntryResult {
    return {
        computed: fromBaseClone(newborn.member, new Set(['catalog'])), // dnr-clone: allow size=one incoming newborn member excluding its immutable catalog; why=initial memo must not alias the shared decode or display
        stagesRun: [], stagesMissing: newborn.pending ? ['creature-base-pending'] : [],
    };
}

export interface BaseRuntimeHooks {
    load(id: string, entity: string): Promise<BaseEnvelope | BaseBusy>;
    compile(envelope: BaseEnvelope): FromBaseEntryResult | Promise<FromBaseEntryResult>;
    /** Synchronous: a repaint may re-adopt, but must never await that adoption. */
    paint(envelope: BaseEnvelope, computed: unknown, prepared?: boolean): void;
    /** Return a detached mutable display; the compiler result remains the read-only memo. */
    preparePaint?(envelope: BaseEnvelope, computed: unknown): Promise<unknown>;
    installPaint?(envelope: BaseEnvelope, wire: string, frame?: AppliedFrame): void;
    installNewEntities?: boolean;
    compileNewEntities?(entities: readonly AppliedNewEntity[], context: BaseEnvelope, bankInputs: (base: BaseEnvelope['base'], metadata: unknown) => void): Promise<readonly AppliedNewEntity[]>;
    bankNewEntities?(entities: readonly AppliedNewEntity[], context: BaseEnvelope): Promise<{ base: BaseEnvelope['base']; metadata: unknown }>;
    recoverInventory?(id: string): void;
    discard?(envelope: BaseEnvelope): void;
    retainSnapshot?(source: unknown, copy: unknown): void;
    copyComputed?(value: unknown): Promise<unknown>;
    savedBase?(envelope: BaseEnvelope): BaseEnvelope['base'];
    canWriteBack?(envelope: BaseEnvelope): boolean;
    writeBack(envelope: BaseEnvelope, computed: unknown, diagnostics?: readonly string[]): Promise<void>;
    refuse(id: string, stages: readonly string[], entity?: string): void;
    diagnostics?(envelope: BaseEnvelope, reasons: readonly string[]): void;
    versionChanged(engine: string): void;
    /** Tests may inject the same save contract as window.ftTryCommitStatusDelete. */
    removeStatus?(statusId: string): Promise<unknown>;
    addStatus?(input: EncumbranceStatusAdd): Promise<unknown>;
    isGmPage?(encounterId: string): boolean;
    loadMembers?(ids: readonly string[], ops: readonly BasePatchOp[]): Promise<RelocationMemberBatch>;
    warn?(message: string): void;
    skipStatusRetirement?(encounterId: string, targetId: string, reason: string): Promise<unknown>;
    /** One browser-owned coverage ledger, shared by the entity and inventory facets. */
    appliedRevision?(id: string): number | undefined;
    observeApplied?(wire: string, held: Iterable<BaseEnvelope>, frame?: AppliedFrameResult): () => void;
    savedSkillGrantPatch?(envelope: BaseEnvelope, ops: readonly BasePatchOp[], output: unknown): Promise<SavedSkillGrantPatch | null>;
    paintSkillGrantPatch?(envelope: BaseEnvelope, patch: SavedSkillGrantPatch): void;
    inventoryMoveContext?(envelope: BaseEnvelope): { envelope: BaseEnvelope; computed: unknown } | undefined;
}

/** A compile hook found the base moved on while it read the catalog: the fresher envelope it holds
 * is a newer revision of the same surface, which the runtime adopts in place of a refusal. */
export class BaseRevisionSuperseded extends Error {
    constructor(readonly envelope: BaseEnvelope) { super('compile-worker-revision-changed'); }
}

export const baseEntityKind = (entity: string): string => entity === 'pc' ? 'creature' : entity;
export const baseEntityKey = (id: string, entity: string): string => `${baseEntityKind(entity)}:${id.toUpperCase()}`;

/** Patch saved floor changes; terrain derives only the named row in the held result. */
const floorPositionColumns = new Set(['inventory_id', 'possess_id', 'x_pos', 'y_pos', 'z_pos',
    'transform_3d', 'inventory_size', 'inventory_deleted', 'v_max', 'v_current']);
export function savedFloorPositionRows(base: BaseEnvelope['base'], ops: readonly BasePatchOp[], computed: unknown): AppliedTouchedRow[] | null {
    const facts = fromBaseRecord(computed) ? computed : {};
    const floor = Array.isArray(computed) ? computed.filter(fromBaseRecord) : readInventoryRows(facts, 'encounter_inventories_arr');
    const hosts = [base, ...(Array.isArray(base.encounter_creatures_arr) ? base.encounter_creatures_arr : [])].filter(fromBaseRecord);
    const nodes = hosts.flatMap(host => [...(fromBaseRecord(host.inventory) ? [host] : []),
        ...(Array.isArray(host.inventory) ? host.inventory.filter(fromBaseRecord) : [])]);
    const rows: AppliedTouchedRow[] = [];
    for (const op of ops) {
        const member = floor.find(row => String(readInventoryField(row, 'inventory_id') ?? '').toUpperCase() === op.row_id.toUpperCase());
        const source = member && { ...member, catalog: facts.catalog ?? member.catalog };
        // Light conditions depend on the source's position; following statuses can move darkness/obscuration too.
        if (source && (Math.abs(Number(layerOf(source, 'bright_light_dim_light'))) > 0
          || phpJsonTruthy(layerOf(source, 'magical_darkness'))
          || Object.values(readCreatureStatuses(source)).filter(fromBaseRecord)
              .some(status => phpJsonTruthy(layerOf(status.status_arr, 'aoe_follows_target'))))) return null;
        if (op.table !== 'inventory' || op.inputs || op.catalog_inputs) return null;
        const node = nodes.find(row => String(readInventoryField(row, 'inventory_id') ?? '').toUpperCase() === op.row_id.toUpperCase());
        if (!node || !member || 'possess_id' in op.cols && String(readInventoryField(node, 'possess_id') ?? '').toUpperCase()
          !== String(readInventoryField({ inventory: op.cols }, 'possess_id') ?? '').toUpperCase()
          || !['x_pos', 'y_pos', 'z_pos'].every(key => !(key in op.cols) || Number.isFinite(Number(readInventoryField({ inventory: op.cols }, key))))) return null;
        // Full SQL echoes also carry unchanged columns and save bookkeeping. Compare
        // against the raw held row, never its lean/derived display mirrors.
        if (Object.keys(op.cols).some(key => !floorPositionColumns.has(key)
            && !['inventory_i', 'inventory_t', 'inventory_t_updated', 'inventory_updated_by_id'].includes(key)
            && !sameSavedInventoryColumn(key, readInventoryField(node, key), readInventoryField({ inventory: op.cols }, key)))) return null;
        const incoming = { inventory: op.cols };
        const saved = Object.fromEntries(Object.keys(op.cols).filter(key =>
            !sameSavedInventoryColumn(key, readInventoryField(node, key), readInventoryField(incoming, key)))
            .map(key => [key, readInventoryField(incoming, key)]));
        const changed = Object.fromEntries(Object.keys(op.cols).filter(key => floorPositionColumns.has(key)
            && (key === 'inventory_id' || key === 'possess_id'
                || !sameSavedInventoryColumn(key, readInventoryField(node, key), readInventoryField(incoming, key))))
            .map(key => [key, readInventoryField(incoming, key)]));
        rows.push({ table: 'inventory', row_id: op.row_id, partial: true,
            base: op.op === 'delete' ? undefined : saved, row: op.op === 'delete' ? { inventory_deleted: 1 } : changed });
    }
    return rows.length ? rows : null;
}

/** Height/drop saves deliver the instance's definition closure too. Identical
 * definitions stay held; only the saved operation columns reach the display. */
async function savedFloorPositionPatch(base: BaseEnvelope['base'], ops: readonly BasePatchOp[], computed: unknown): Promise<{ rows: AppliedTouchedRow[]; inputs?: BaseEnvelope['base'] } | null> {
    const compact = savedFloorPositionRows(base, ops, computed);
    if (compact) return { rows: compact };
    if (ops.some(op => !['inventory', 'items', 'item_layer_types'].includes(op.table))
        || !await relocationDefinitionsUnchanged(base, ops)) return null;
    const rows = savedFloorPositionRows(base, ops.filter(op => op.table === 'inventory').map(op => ({ ...op, catalog_inputs: undefined })), computed);
    if (!rows) return null;
    let inputs: BaseEnvelope['base'] = {};
    for (const op of ops) if (op.catalog_inputs) inputs = mergeInventoryMetadata(inputs, op.catalog_inputs);
    return { rows, inputs };
}

/** Bundle-neutral state factory. The websocket entry owns the only browser instance. */
export function createBaseRuntime(engine: string, hooks: BaseRuntimeHooks) {
    const held = new Map<string, BaseEnvelope>();
    // Deleted facets stay retired even when an older SELECT/worker finishes.
    const retiredFacets = new Set<string>();
    const flights = new Map<string, { promise: Promise<void>; writer: boolean; repaint: boolean; envelope: BaseEnvelope }>();
    const pending = new Map<string, { promise: Promise<void>; required: number; writer: boolean }>();
    const relocationFlights = new Map<string, Promise<RelocationMemberBatch>>();
    const savedMoveReceipts = new WeakMap<BaseEnvelope, { revision: number; ops: readonly BasePatchOp[] }>();
    const compiled = new Map<string, { entity: string; id: string; result: FromBaseEntryResult }>();
    const conditionFlights = new Map<string, Promise<void>>();
    const conditionMetadata = new WeakMap<object, ReturnType<typeof entityRuntimeMetadata>>();
    const winners = new Map<string, { key: string; base: BaseEnvelope['base'] }>();
    // A correction can carry newer saved rows without advancing the enclosing revision.
    // Its held base and memo own those values until a newer revision arrives.
    const correctedBases = new WeakSet<BaseEnvelope['base']>();
    const won = (envelope: BaseEnvelope, key: string) => {
        const winner = winners.get(baseEntityKey(envelope.entity_id, envelope.entity));
        return winner?.key === key && winner.base === envelope.base;
    };
    const counters = { compiles: 0, memoHits: 0, serverCacheHits: 0, evictions: 0, refusals: 0 };
    const identities = new WeakMap<object, number>();
    let nextIdentity = 0;
    let paused = false;
    const idOf = (id: string): string => id.toUpperCase();
    const get = (id: string, entity: string): BaseEnvelope | undefined => held.get(baseEntityKey(id, entity));
    const forId = (id: string, entity?: string): BaseEnvelope[] => [...held.values()].filter(envelope =>
        idOf(envelope.entity_id) === idOf(id) && (entity === undefined || baseEntityKind(envelope.entity) === baseEntityKind(entity)));
    function identity(value: unknown): unknown {
        if ((typeof value !== 'object' || value === null) && typeof value !== 'function') return [typeof value, value];
        let token = identities.get(value);
        if (token === undefined) { token = ++nextIdentity; identities.set(value, token); }
        return [typeof value, token];
    }
    function memoKey(envelope: BaseEnvelope): string {
        const catalogVersion = envelope.catalog_version ?? null;
        // Revision/catalog fence the saved rows and scoped definitions. Shared metadata is immutable;
        // its identity also catches replaced/missing metadata without hashing a tree on every adopt.
        // Inline, unversioned catalogs additionally require the identical base object. Viewer verdicts repaint below.
        return JSON.stringify([baseEntityKind(envelope.entity), idOf(envelope.entity_id), envelope.base_rev, envelope.engine,
            catalogVersion, envelope.base.dependency_revs ?? {}, envelope.base.dependency_revs_invalidate_only ?? {},
            catalogVersion === null ? identity(envelope.base) : null, identity(envelope.metadata)]);
    }
    function evict(key: string): void {
        if (compiled.delete(key)) counters.evictions++;
    }
    async function copyComputed(value: unknown): Promise<unknown> {
        const copy = hooks.copyComputed ? await hooks.copyComputed(value) : fromBaseClone(value);
        hooks.retainSnapshot?.(value, copy); return copy;
    }
    const background = new Set<string>();
    const appliedRecoveries = new Map<string, Promise<void>>();
    const appliedMinimum = new Map<string, number>();
    function refuse(id: string, stages: readonly string[], entity: string): void {
        if (background.has(baseEntityKey(id, entity))) return;
        const required = states.get(baseEntityKey(id, entity))?.required;
        if (required !== undefined && required <= (hooks.appliedRevision?.(idOf(id)) ?? -1)) return;
        for (const [key, entry] of compiled) if (entry.id === idOf(id) && baseEntityKind(entry.entity) === baseEntityKind(entity)) evict(key);
        counters.refusals++; hooks.refuse(id, stages, entity);
    }
    const retirements = createEncumbranceRetirementRuntime({
        current: (id, entity) => paused ? undefined : get(id, entity),
        isGmPage: encounterId => {
            if (hooks.isGmPage) return hooks.isGmPage(encounterId);
            const page = globalThis as { FateTide?: { user?: { isGM?: boolean } }; ftGetActiveEncounterId?: () => string | null };
            return page.FateTide?.user?.isGM === true && idOf(page.ftGetActiveEncounterId?.() ?? '') === idOf(encounterId);
        },
        skip: async (encounterId, targetId, reason) => {
            if (hooks.skipStatusRetirement) { await hooks.skipStatusRetirement(encounterId, targetId, reason); return; }
            const bridge = (globalThis as { ftCommitGmSkip?: (encounterId: string, targetId: string, reason: string, expectedRev?: number, reportFailure?: boolean) => Promise<unknown> }).ftCommitGmSkip;
            if (bridge) await bridge(encounterId, targetId, reason, undefined, false);
            else skipUnported(reason, { encounterId, targetId });
        },
        remove: async statusId => {
            if (hooks.removeStatus) return hooks.removeStatus(statusId);
            const bridge = (globalThis as { ftTryCommitStatusDelete?: (input: { statusId: string; scope: 'single'; background: true }) => Promise<unknown> }).ftTryCommitStatusDelete;
            return bridge ? bridge({ statusId, scope: 'single', background: true }) : 'unavailable';
        },
        add: async input => {
            if (hooks.addStatus) return hooks.addStatus(input);
            const bridge = (globalThis as { ftTryCommitStatusAdd?: (input: EncumbranceStatusAdd) => Promise<unknown> }).ftTryCommitStatusAdd;
            return bridge ? bridge(input) : 'unavailable';
        },
    });
    function checkEngine(next: string): boolean {
        if (paused) return false;
        // PHP sends no identity during its bounded, unmatched-sidecar publish window.
        if (next === '' || next === engine) return true;
        paused = true;
        counters.evictions += compiled.size; compiled.clear();
        hooks.versionChanged(next); return false;
    }
    const isCurrent = (envelope: BaseEnvelope): boolean => {
        const current = get(envelope.entity_id, envelope.entity);
        return !paused && current !== undefined && current.base_rev === envelope.base_rev
            && current.base === envelope.base && current.metadata === envelope.metadata
            && (current.catalog_version ?? null) === (envelope.catalog_version ?? null);
    };
    /** `repaint: false` persists a revision the surfaces already show (a memo or server-cache hit) without painting it again. */
    async function compile(envelope: BaseEnvelope, writer: boolean, repaint = true): Promise<void> {
        if (!checkEngine(envelope.engine)) return;
        if (inputBanks.has(envelope) && (!await awaitBirthInputs(envelope) || get(envelope.entity_id, envelope.entity) !== envelope)) return;
        envelope = { ...envelope };
        const key = memoKey(envelope), previous = flights.get(key);
        if (previous) { previous.writer ||= writer; previous.repaint ||= repaint; if (previous.envelope.base !== envelope.base) previous.envelope = envelope; return previous.promise; }
        const flight = { writer, repaint, envelope, promise: Promise.resolve() };
        flight.promise = (async () => {
            // Same-revision SELECTs may replace raw status context. Coalesce the
            // latest input behind the existing flight, then compile that input.
            // A paint skipped for a persist-only flight is owed again when a repainting compile joined after the skip.
            let input: BaseEnvelope, skipped: boolean;
            do { input = flight.envelope; skipped = await runCompile(input, key, () => flight.writer, () => flight.repaint); }
            while ((input !== flight.envelope || (skipped && flight.repaint)) && isCurrent(flight.envelope));
        })().finally(() => { if (flights.get(key) === flight) flights.delete(key); });
        flights.set(key, flight);
        return flight.promise;
    }
    function currentComputedCache(envelope: BaseEnvelope): BaseEnvelope['cache'] | undefined {
        const cache = envelope.cache;
        return cache && cache.facet === (envelope.entity === 'inventory' ? 'inventory' : 'entity')
            && baseEntityKind(cache.entity ?? '') === baseEntityKind(envelope.entity)
            && cache.base_rev === envelope.base_rev && cache.engine === engine
            && (cache.catalog_version ?? null) === (envelope.catalog_version ?? null) ? cache : undefined;
    }
    /** Answers whether it skipped the paint (`repaint()` false on a memo or server-cache hit). */
    async function runCompile(envelope: BaseEnvelope, key: string, writer: () => boolean, repaint: () => boolean = () => true): Promise<boolean> {
        const compileId = idOf(envelope.entity_id);
        const cache = currentComputedCache(envelope);
        if (cache) {
            counters.serverCacheHits++;
            const missing = retirements.settle(envelope, cache.computed, writer());
            if (missing.length) { refuse(envelope.entity_id, missing, envelope.entity); return false; }
            if (Array.isArray(cache.stage_diagnostics) && cache.stage_diagnostics.length)
                hooks.diagnostics?.(envelope, cache.stage_diagnostics.filter(reason => typeof reason === 'string'));
            winners.set(baseEntityKey(compileId, envelope.entity), { key, base: envelope.base });
            // The server already holds this computed: a server-cache hit never writes back, painted or not.
            if (!repaint()) return true;
            hooks.paint(envelope, cache.computed); return false;
        }
        let result: FromBaseEntryResult;
        const previous = compiled.get(key);
        // Paint/optimistic UI code may edit its own returned tree. Keep the memo
        // detached and give each repaint its own values, like a fresh compile.
        try {
            if (previous) {
                counters.memoHits++;
                compiled.delete(key); compiled.set(key, previous); // LRU touch.
                if (!repaint()) {
                    // This revision is already painted: keep only the writer's duties for it, never repaint (persistTouched). Both
                    // retirements.settle and writeBack only read the memo (plans queued, a serialized snapshot), so it is not copied.
                    // Applied deltas still owe persistence; same-revision
                    // corrections retain their separate, non-writing contract.
                    if (!isCurrent(envelope) || correctedBases.has(envelope.base)) return false;
                    const missing = retirements.settle(envelope, previous.result.computed, writer());
                    if (missing.length) { refuse(envelope.entity_id, missing, envelope.entity); return false; }
                    if (writer()) {
                        try { void hooks.writeBack({ ...envelope }, previous.result.computed, previous.result.stageDiagnostics).catch(() => {}); }
                        catch { /* A shared cache miss never invalidates this page's computed truth. */ }
                    }
                    return true;
                }
                result = hooks.preparePaint ? previous.result : { ...previous.result, computed: await copyComputed(previous.result.computed) };
            } else { counters.compiles++; result = await hooks.compile(envelope); }
        }
        catch (error) {
            if (!isCurrent(envelope) || won(envelope, key)) return false;
            // A commit landed between this page's revision and its catalog read. The fresher base the
            // read returned is this surface's next revision: follow it; never refuse a moved-on base.
            if (error instanceof BaseRevisionSuperseded && error.envelope.entity === envelope.entity
                && idOf(error.envelope.entity_id) === compileId && error.envelope.base_rev > envelope.base_rev) {
                await adopt(error.envelope, writer());
                return false;
            }
            refuse(envelope.entity_id, [error instanceof Error ? error.message : 'base-compiler-error'], envelope.entity);
            return false;
        }
        if (!isCurrent(envelope) || won(envelope, key)) return false;
        if (result.stageDiagnostics?.length) hooks.diagnostics?.(envelope, result.stageDiagnostics);
        if (result.computed === undefined || result.stagesMissing.length) {
            refuse(envelope.entity_id, result.stagesMissing.length ? result.stagesMissing : ['compiler-output'], envelope.entity);
            return false;
        }
        const copy = !previous && !hooks.preparePaint ? await copyComputed(result.computed) : result.computed;
        if (!isCurrent(envelope) || won(envelope, key)) return false;
        const missing = retirements.settle(envelope, result.computed, writer());
        if (missing.length) { refuse(envelope.entity_id, missing, envelope.entity); return false; }
        const display = hooks.preparePaint ? await hooks.preparePaint(envelope, result.computed) : result.computed;
        if (!isCurrent(envelope) || won(envelope, key)) return false;
        if (!previous) {
            const id = idOf(envelope.entity_id);
            // Keep only the current key per facet; revision churn must not displace other held facets.
            for (const [oldKey, entry] of compiled) if (baseEntityKind(entry.entity) === baseEntityKind(envelope.entity) && entry.id === id) evict(oldKey);
            compiled.set(key, { entity: envelope.entity, id, result: { ...result, computed: copy } });
            // Full pushes revisit every held facet; grow the bound with the page at store time.
            const memoCap = Math.max(32, 2 * held.size);
            while (compiled.size > memoCap) evict(compiled.keys().next().value!);
        }
        hooks.paint(envelope, display, !!hooks.preparePaint);
        if (writer()) {
            // The page's truth is already painted. An optional cache upload cannot hold adoption or recovery open.
            try { void hooks.writeBack({ ...envelope }, result.computed, result.stageDiagnostics).catch(() => {}); }
            catch { /* A shared cache miss never invalidates this page's computed truth. */ }
        }
        return false;
    }
    async function adopt(envelope: BaseEnvelope | BaseBusy, writer = 'cold_compiler' in envelope && envelope.cold_compiler === true): Promise<void> {
        if (!checkEngine(envelope.engine)) return;
        if (retiredFacets.has(baseEntityKey(envelope.entity_id, envelope.entity))) return;
        if ('resync' in envelope) { await resync(envelope.entity_id, envelope.entity, 0, writer); return; }
        const id = baseEntityKey(envelope.entity_id, envelope.entity), previous = held.get(id);
        if (previous && previous.base_rev > envelope.base_rev) {
            // A late raw snapshot can register a new surface after the saved-row relay.
            await compile(previous, false); return;
        }
        if (previous?.base_rev === envelope.base_rev && correctedBases.has(previous.base)) {
            envelope = { ...previous, viewer: envelope.viewer ?? previous.viewer,
                viewer_permissions: envelope.viewer_permissions ?? previous.viewer_permissions };
        }
        // Broadcasts omit the writer's verdict. Keep this viewer's HTTP verdict until
        // a page read/resync supplies a new one, without changing the incoming frame.
        if (envelope.viewer_permissions === undefined && previous?.viewer_permissions !== undefined)
            envelope = { ...envelope, viewer_permissions: previous.viewer_permissions };
        if (envelope.viewer === undefined && previous?.viewer !== undefined)
            envelope = { ...envelope, viewer: previous.viewer };
        if (baseEntityKind(envelope.entity) === 'creature') {
            envelope.base = contextualMemberBase(envelope.base);
        }
        held.set(id, envelope);
        // Prime the saved-row identities, including cache-hit adoptions whose
        // compiler never visits these rows. Later frames replace only due rows.
        if (envelope.entity === 'encounter') hasSceneConditions(envelope.base);
        deliver(id, envelope.base_rev);
        reconcileMembers(envelope);
        // A same-revision adoption may register another surface. Only an active
        // compile competes with a computed-ready relay; completed memos repaint.
        if (!flights.has(memoKey(envelope))) winners.delete(id);
        await compile(envelope, writer);
    }
    /** Per-entity revision state, shared by every recovery and generation-fenced. `delivered` is the newest
     * revision an authoritative source handed this page for the entity (its own adoption, a saved-row patch, an
     * enclosing read's member row); `required` is the highest revision an op or recovery still demands above it.
     * Every delivery, retirement and raise bumps `generation`. A recovery registers its requirement when it STARTS
     * and captures the generation; on completion it never writes the record — a stale completion only settles its
     * awaiters — so nothing a delivery retired can be resurrected or lowered. Readiness reads the current record. */
    type RevisionState = { delivered: number; generation: number; required?: number; entity?: string };
    const states = new Map<string, RevisionState>();
    const keysFor = (id: string, entity?: string): string[] => entity !== undefined ? [baseEntityKey(id, entity)]
        : [...new Set([...held.keys(), ...states.keys(), ...recoveries.keys()])].filter(key => key.endsWith(`:${idOf(id)}`));
    const stateOf = (key: string): RevisionState => {
        let state = states.get(key);
        if (!state) { state = { delivered: held.get(key)?.base_rev ?? -1, generation: 0 }; states.set(key, state); }
        return state;
    };
    /** An authoritative revision for the entity: it retires any requirement it meets. */
    function deliver(key: string, revision: number): void {
        const state = stateOf(key);
        let changed = false;
        // Every accepted delivery advances the generation, an equal revision too: a refresh at the held revision makes an older read's failure stale.
        if (revision >= state.delivered) { state.delivered = revision; changed = true; }
        if (state.required !== undefined && state.required <= state.delivered) { delete state.required; delete state.entity; changed = true; }
        if (changed) state.generation++;
    }
    /** A demand above what was delivered: raised, never lowered; already-met demands are no-ops. */
    function require(key: string, revision: number, entity: string): void {
        const state = stateOf(key);
        if (revision <= state.delivered || (state.required !== undefined && state.required >= revision)) return;
        state.required = revision; state.entity ??= entity; state.generation++;
    }
    /** The requirement still open above the held revision, if any. */
    function gap(id: string, entity?: string): number | undefined {
        if (entity && background.has(baseEntityKey(id, entity))) return;
        const gaps = keysFor(id, entity).flatMap(key => {
            const required = states.get(key)?.required;
            return required !== undefined && Math.max(held.get(key)?.base_rev ?? -1, hooks.appliedRevision?.(idOf(id)) ?? -1) < required ? [required] : [];
        });
        return gaps.length ? Math.max(...gaps) : undefined;
    }
    /** Every recovery in flight, by entity id, so an action boundary can join a read it did not start. */
    const recoveries = new Map<string, Set<Promise<void>>>();
    function recovering(id: string, entity?: string): Promise<void> | undefined {
        const runs = keysFor(id, entity).flatMap(key => [...recoveries.get(key) ?? [], ...appliedRecoveries.has(key) ? [appliedRecoveries.get(key)!] : []]);
        return runs.length ? Promise.allSettled(runs).then(() => recovering(id, entity)) : undefined;
    }
    /** Start, or join, the recovery of an open requirement. A refused action starts one so its retry can heal. */
    function recover(id: string, entity?: string): Promise<void> | undefined {
        const runs = keysFor(id, entity).flatMap(key => {
            const kind = held.get(key)?.entity ?? states.get(key)?.entity ?? entity ?? 'creature';
            const required = gap(id, kind), pending = recovering(id, kind);
            return pending ? [pending] : required === undefined ? [] : [resync(id, kind, required, false)];
        });
        return runs.length ? Promise.all(runs).then(() => {}) : undefined;
    }
    /** An enclosing read (an encounter with its saved members) is authoritative for each member's revision: it is
     * delivered for every member, retiring requirements it meets; a member still below its requirement recovers its own facet. */
    function reconcileMembers(envelope: BaseEnvelope): void {
        const members = envelope.base.encounter_creatures_arr;
        if (!Array.isArray(members)) return;
        for (const member of members as Array<{ entity_id?: unknown; base_rev?: unknown }>) {
            if (!member || typeof member !== 'object' || typeof member.entity_id !== 'string' || typeof member.base_rev !== 'number') continue;
            const key = baseEntityKey(member.entity_id, 'creature');
            deliver(key, member.base_rev);
            if (gap(member.entity_id, 'creature') !== undefined) void recover(member.entity_id, 'creature');
        }
    }
    function resync(id: string, entity = 'inventory', required = 0, writer = false): Promise<void> {
        const key = baseEntityKey(id, entity);
        if (retiredFacets.has(key)) return Promise.resolve();
        if (required > 0) require(key, required, entity);
        const previous = pending.get(key);
        if (previous) {
            previous.required = Math.max(required, previous.required); previous.writer ||= writer;
            const current = held.get(key), flight = current && flights.get(memoKey(current));
            if (flight) flight.writer ||= writer;
            return previous.promise;
        }
        const job = { required, writer, promise: Promise.resolve() };
        // Capture the generation before yielding: even an equal-revision delivery
        // immediately after resync starts must make this read's failure obsolete.
        pending.set(key, job);
        const run = recoverRevision(idOf(id), entity, job);
        const set = recoveries.get(key) ?? new Set<Promise<void>>();
        set.add(run); recoveries.set(key, set);
        job.promise = run.finally(() => {
            if (pending.get(key) === job) pending.delete(key);
            set.delete(run); if (!set.size && recoveries.get(key) === set) recoveries.delete(key);
        });
        return job.promise;
    }
    async function recoverRevision(id: string, entity: string, job: { required: number; writer: boolean }): Promise<void> {
        const key = baseEntityKey(id, entity);
        const captured = stateOf(key).generation;
        const stale = (): boolean => stateOf(key).generation !== captured;
        const open = (): boolean => gap(id, entity) !== undefined;
        let busy = false, failure: string | undefined;
        const attempts = background.has(key) ? 4 : 3;
        for (let attempt = 0; attempt < attempts; attempt++) {
            let envelope: BaseEnvelope | BaseBusy;
            try { envelope = await hooks.load(id, entity); }
            catch (error) {
                failure = error instanceof Error ? error.message : 'base-read-failed';
                if (!stale()) { refuse(id, [failure], entity); return; }
                // A delivery landed meanwhile: a retired requirement makes this completion a no-op; an open one earns one more bounded read.
                if (!open()) return;
                continue;
            }
            if (retiredFacets.has(key)) return;
            if ('resync' in envelope) {
                if (!checkEngine(envelope.engine)) return;
                busy = true;
                if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 50 * (attempt + 1)));
                continue;
            }
            const minimum = appliedMinimum.get(key);
            if (minimum !== undefined && Number(encounterCreatureField(envelope.base.encounter_creatures, 'movement_rev') ?? 0) < minimum) {
                if (attempt + 1 < attempts) await new Promise(resolve => setTimeout(resolve, 500 * 2 ** attempt));
                continue;
            }
            if (envelope.base_rev < Math.max(job.required, gap(id, entity) ?? 0)) continue;
            // Combat relays can change statuses at the same base revision. An explicit reload must compile its fresh saved rows.
            for (const [memo, entry] of compiled) if (baseEntityKind(entry.entity) === baseEntityKind(envelope.entity) && entry.id === id) evict(memo);
            await adopt(envelope, job.writer);
            if (gap(id, entity) !== undefined) continue;
            return;
        }
        if (stale() && !open()) return;
        refuse(id, [busy ? 'base-busy' : failure ?? 'base-revision-recovery'], entity);
    }
    function recoverApplied(id: string, entity: string, minimumMovement = 0): void {
        const key = baseEntityKey(id, entity);
        appliedMinimum.set(key, Math.max(appliedMinimum.get(key) ?? 0, minimumMovement));
        if (background.has(key)) return;
        background.add(key);
        const job = resync(id, entity).then(() => {
            const recovered = get(id, entity);
            if (!recovered || Number(encounterCreatureField(recovered.base.encounter_creatures, 'movement_rev') ?? 0) < (appliedMinimum.get(key) ?? 0)) return;
            const member = compiled.get(memoKey(recovered))?.result.computed ?? recovered.cache?.computed;
            if (!member || typeof member !== 'object') return;
            // A member read repairs that member in every enclosing memo, without reloading/recompiling its encounter.
            for (const host of held.values()) {
                if (host.entity !== 'encounter' || !Array.isArray(host.base.encounter_creatures_arr)) continue;
                const has = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object'
                    && String((value as Record<string, unknown>).entity_id ?? '').toUpperCase() === idOf(id);
                if (!host.base.encounter_creatures_arr.some(has)) continue;
                const oldKey = memoKey(host), previous = compiled.get(oldKey)?.result;
                if (!previous?.computed || typeof previous.computed !== 'object') continue;
                const output = previous.computed as Record<string, unknown>;
                if (!Array.isArray(output.encounter_creatures_arr)) continue;
                const rowId = (value: unknown): string => String(encounterCreatureField((value as Record<string, unknown>).encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
                host.base = { ...host.base, encounter_creatures_arr: host.base.encounter_creatures_arr.map(row => has(row) ? recovered.base : row) };
                evict(oldKey);
                const computed = { ...output, encounter_creatures_arr: output.encounter_creatures_arr.map(row => rowId(row) === idOf(id) ? member : row) };
                compiled.set(memoKey(host), { entity: host.entity, id: idOf(host.entity_id), result: { ...previous, computed } });
                host.cache = null;
                winners.set(baseEntityKey(host.entity_id, host.entity), { key: memoKey(host), base: host.base });
                hooks.discard?.(host);
                hooks.paint(host, computed);
            }
        }).catch(() => {}).finally(() => { background.delete(key); appliedMinimum.delete(key); appliedRecoveries.delete(key); });
        appliedRecoveries.set(key, job);
    }
    /** Bank authoritative correction rows in the existing raw input and memo. No
     * synthetic revision, shadow position store, network read or cache write-back. */
    function correctSavedRows(id: string, rows: readonly AppliedTouchedRow[], correction: SavedRowCorrection = {}): void {
        const frame: AppliedFrame = { kind: 'applied', operation_id: '', encounter_id: idOf(id), actor_id: '', rev: 0,
            member_revs: {}, rows, computed: {}, new_entities: [], deletes: correction.deletes ?? [] };
        if (correction.operationId) {
            installedOperations.add(correction.operationId);
            if (installedOperations.size > 512) installedOperations.delete(installedOperations.values().next().value!);
        }
        if (!rows.length && !frame.deletes.length) return;
        const targets = actionBarsOnlyFrame(frame) ? [...held.values()].filter(envelope => holdsSavedActionBars(envelope.base, frame)) : forId(id);
        for (const envelope of targets) {
            const base = applyAppliedFrame(frame, envelope.base, true, false).value as BaseEnvelope['base'];
            if (base === envelope.base) continue;
            const oldKey = memoKey(envelope), previous = compiled.get(oldKey)?.result;
            const output = previous?.computed ?? envelope.cache?.computed;
            envelope.base = base; envelope.cache = null;
            correctedBases.add(base);
            evict(oldKey);
            // Replacing the base identity fences a prepared paint already in flight.
            hooks.discard?.(envelope);
            if (output !== undefined) {
                const key = memoKey(envelope);
                compiled.set(key, { entity: envelope.entity, id: idOf(envelope.entity_id), result: { ...previous,
                    computed: applyAppliedFrame(frame, output, false, false, idOf(id), false, envelope.metadata as unknown as import('./from-base-encounter-terrain').EncounterTerrainMetadata).value,
                    stagesRun: previous?.stagesRun ?? [], stagesMissing: previous?.stagesMissing ?? [] } });
                winners.set(baseEntityKey(envelope.entity_id, envelope.entity), { key, base });
            } else void compile(envelope, false);
        }
    }
    const installedOperations = new Set<string>();
    /** A saved layout already patched these inputs. Move their memo/revision fences in place. */
    function acknowledgeSavedRevisions(revisions: Readonly<Record<string, number>>): void {
        // Enclosing/member facets may borrow the same dependency map. Capture all
        // old keys before changing it, and bank new keys only after every edit.
        const memos = [...held.values()].map(envelope => {
            const oldKey = memoKey(envelope);
            const cache = currentComputedCache(envelope);
            const previous = compiled.get(oldKey) ?? (cache ? { entity: envelope.entity, id: idOf(envelope.entity_id), result: {
                computed: cache.computed, stagesRun: [], stagesMissing: [], stageDiagnostics: cache.stage_diagnostics } } : undefined);
            return { envelope, oldKey, previous };
        });
        const changed: typeof memos = [];
        for (const memo of memos) {
            const { envelope } = memo;
            const id = idOf(envelope.entity_id), revision = revisions[id];
            if (!Number.isSafeInteger(revision) || revision! <= envelope.base_rev) continue;
            if (revision! > envelope.base_rev + 1) { recoverApplied(id, envelope.entity); continue; }
            envelope.base_rev = revision!;
            envelope.base.base_rev = revision;
            const hosts = [envelope.base, ...(Array.isArray(envelope.base.encounter_creatures_arr) ? envelope.base.encounter_creatures_arr : [])].filter(fromBaseRecord);
            for (const host of hosts) {
                const memberRev = revisions[idOf(String(host.entity_id ?? ''))];
                if (memberRev !== undefined && 'base_rev' in host) host.base_rev = Math.max(Number(host.base_rev ?? 0), memberRev);
                if (fromBaseRecord(host.dependency_revs)) for (const dependency of Object.keys(host.dependency_revs)) {
                    const next = revisions[dependency];
                    if (next !== undefined) host.dependency_revs[dependency] = Math.max(Number(host.dependency_revs[dependency] ?? 0), next);
                }
            }
            if (typeof envelope.base.catalog_ref === 'string') envelope.base.catalog_ref = envelope.base.catalog_ref.replace(/:\d+$/, `:${revision}`);
            changed.push(memo);
        }
        for (const { envelope, oldKey, previous } of changed) {
            const id = idOf(envelope.entity_id);
            evict(oldKey);
            const key = memoKey(envelope);
            if (previous) compiled.set(key, previous);
            envelope.cache = null;
            winners.set(baseEntityKey(id, envelope.entity), { key, base: envelope.base });
            deliver(baseEntityKey(id, envelope.entity), envelope.base_rev);
            hooks.discard?.(envelope);
        }
    }
    const birthFlights = new Map<string, Promise<void>>();
    // Jobs belong to exact envelopes, rather than ids that a resync can reuse.
    type InputBank = { promise?: Promise<void>; failure?: string; retryAt: number; active(): boolean; retry(): void };
    const inputBanks = new WeakMap<BaseEnvelope, Map<string, InputBank>>();
    async function awaitBirthInputs(envelope: BaseEnvelope): Promise<boolean> {
        const jobs = inputBanks.get(envelope);
        if (!jobs) return true;
        // A later compile may retry a failed keyed read. Joining an in-flight
        // failure never retries within the same compile, and both facets share
        // the job/cooldown, so busy saved-row patches cannot flood the reader.
        for (const job of jobs.values()) if (job.active() && job.failure && !job.promise && Date.now() >= job.retryAt) job.retry();
        for (;;) {
            const waiting: Promise<void>[] = [];
            for (const job of jobs.values()) if (job.active() && job.promise) waiting.push(job.promise);
            if (!waiting.length) break;
            await Promise.all(waiting);
        }
        for (const job of jobs.values()) if (job.active() && job.failure) return false;
        return true;
    }
    function bankCompletedBirths(entities: readonly AppliedNewEntity[], context: BaseEnvelope): void {
        if (!hooks.bankNewEntities || !entities.length) return;
        const targets = entities.flatMap(entity => {
            const envelope = get(entity.entity_id, entity.entity);
            return envelope ? [{ entity, envelope }] : [];
        });
        const active = (id: string): boolean => get(context.entity_id, context.entity) === context
            && Array.isArray(context.base.encounter_creatures_arr) && context.base.encounter_creatures_arr.some(member => fromBaseRecord(member)
                && idOf(String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id'))) === idOf(id));
        const jobs = targets.map(({ entity, envelope }) => {
            const job: InputBank = {
                active: () => active(entity.entity_id), retryAt: 0, retry: () => start(),
            };
            for (const target of [context, envelope]) {
                let jobs = inputBanks.get(target);
                if (!jobs) inputBanks.set(target, jobs = new Map());
                jobs.set(entity.entity_id, job);
            }
            return { entity, envelope, job };
        });
        let failures = 0;
        function start(): void {
            const flight = Promise.resolve().then(async () => {
                if (!jobs.some(({ job }) => job.active())) return;
                const selected = await hooks.bankNewEntities!(entities, context);
                const live = jobs.filter(({ job }) => job.active());
                if (!live.length) return;
                for (const envelope of [context, ...live.filter(({ entity, envelope }) => get(entity.entity_id, entity.entity) === envelope).map(target => target.envelope)]) {
                    const oldKey = memoKey(envelope), previous = compiled.get(oldKey);
                    // Current saved rows win; banking never changes inventory order or compiled values.
                    const shared = encounterMemberSharedInputs(mergeInventoryMetadata(selected.base, envelope.base));
                    delete shared.inventory_order;
                    Object.assign(envelope.base, shared);
                    envelope.metadata ??= selected.metadata;
                    const key = memoKey(envelope);
                    if (key !== oldKey) { evict(oldKey); if (previous) compiled.set(key, previous); }
                    if (previous) winners.set(baseEntityKey(envelope.entity_id, envelope.entity), { key, base: envelope.base });
                }
            }).catch(error => {
                const retryAt = failures++ === 0 ? 0 : Date.now() + 5000;
                for (const { entity, job } of jobs) if (job.active()) {
                    job.failure = error instanceof Error ? error.message : 'spawn:input-banking';
                    job.retryAt = retryAt;
                    refuse(entity.entity_id, [job.failure], entity.entity);
                }
            }).finally(() => {
                for (const { entity, envelope, job } of jobs) {
                    delete job.promise;
                    if (!job.failure || !job.active()) for (const target of [context, envelope]) {
                        const bank = inputBanks.get(target);
                        if (bank?.get(entity.entity_id) === job) bank.delete(entity.entity_id);
                        if (!bank?.size) inputBanks.delete(target);
                    }
                    if (birthFlights.get(entity.entity_id) === flight) birthFlights.delete(entity.entity_id);
                }
            });
            for (const { entity, job } of jobs) { delete job.failure; job.promise = flight; birthFlights.set(entity.entity_id, flight); }
        }
        start();
    }
    function retireFacet(id: string, entity: string): void {
        const key = baseEntityKey(id, entity), envelope = held.get(key);
        retiredFacets.add(key);
        if (envelope) { evict(memoKey(envelope)); hooks.discard?.(envelope); }
        held.delete(key); winners.delete(key);
        const state = stateOf(key);
        delete state.required; delete state.entity; state.generation++;
    }
    function hasSceneConditions(base: BaseEnvelope['base']): boolean {
        const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
        return Array.isArray(base.encounter_creatures_arr)
            && base.encounter_creatures_arr.some(member => fromBaseRecord(member) && memberReadsSceneConditions(member, catalog));
    }
    function refreshSceneConditions(envelope: BaseEnvelope, beforeBase: BaseEnvelope['base'], previous: unknown): void {
        if (envelope.entity !== 'encounter' || !fromBaseRecord(previous) || !fromBaseRecord(envelope.metadata)
            || !hasSceneConditions(envelope.base)) return;
        const id = idOf(envelope.entity_id), pinnedBase = envelope.base;
        const preceding = conditionFlights.get(id);
        const work = Promise.resolve(preceding).then(async () => {
            if (envelope.base !== pinnedBase || paused) return;
            const output = compiled.get(memoKey(envelope))?.result.computed;
            if (!fromBaseRecord(output)) return;
            let metadata = conditionMetadata.get(envelope.metadata as object);
            if (!metadata) {
                metadata = entityRuntimeMetadata(envelope.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
                conditionMetadata.set(envelope.metadata as object, metadata);
            }
            const patch = await savedSummonConditionPatch(envelope, beforeBase, previous, output, metadata.player);
            if (!patch || envelope.base !== pinnedBase) return;
            for (const section of patch.sections) {
                const member = (patch.computed.encounter_creatures_arr as Record<string, unknown>[]).find(row =>
                    idOf(String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'))) === section.id)!;
                patchMemberSections(section.id, (base, heldMember) => ({
                    base: fromBaseRecord(base.encounter_context) ? contextualMemberBase(base) : base,
                    computed: { ...heldMember, computed: member.computed,
                        ...(section.conditionLayers && Object.keys(section.conditionLayers).length
                            ? { item_layer_types: { ...fromBaseRecord(heldMember.item_layer_types) ? heldMember.item_layer_types : {}, ...section.conditionLayers } } : {}) } }));
            }
            hooks.paintSkillGrantPatch?.(envelope, patch);
        }).catch(error => hooks.refuse(id, [error instanceof Error ? error.message : 'summon-within:condition-delta'], 'encounter'));
        conditionFlights.set(id, work);
        void work.finally(() => { if (conditionFlights.get(id) === work) conditionFlights.delete(id); });
    }
    function install(wire: string, savedEnvelope?: BaseEnvelope, savedRevision?: number, savedDependencies?: Readonly<Record<string, number>>, decoded?: AppliedFrameResult, layoutRevisions?: Readonly<Record<string, number>>, savedInputs?: BaseEnvelope['base']): void {
        let frame: AppliedFrameResult;
        try { frame = decoded ?? parsedAppliedFrame(wire); } catch { return; }
        if (installedOperations.has(frame.operation_id)) return;
        installedOperations.add(frame.operation_id);
        if (installedOperations.size > 512) installedOperations.delete(installedOperations.values().next().value!);
        if ('cache_ref' in frame) { for (const id of Object.keys(frame.member_revs)) recoverApplied(id, 'creature', frame.member_revs[id] ?? 0); return; }
        frame = expandAppliedFrameInputs(frame, true);
        for (const removed of frame.deletes) if (removed.table === 'inventory'
            // A spill may retire a former placement and birth the same live row
            // under its new holder. That row's own contents facet stays alive.
            && !frame.new_inventory?.some(birth => birth.base.some(row => idOf(String(readInventoryField(row, 'inventory_id') ?? '')) === idOf(removed.row_id)))) {
            retireFacet(removed.row_id, 'inventory');
        }
        const birthInputs = new Map(frame.new_entities.map(entity => [entity.entity_id, entity.base]));
        const context = get(frame.encounter_id, 'encounter') ?? get(frame.encounter_id, 'location');
        frame = { ...frame, new_entities: frame.new_entities.map(entity => ({ ...entity,
            base: ownedBirthBase(entity.base, context?.base ?? {}) })) };
        const cardRevisions = frame.operation_id.startsWith('card-edit:') && fromBaseRecord(frame.inputs?.card_edit_base_revs)
            ? frame.inputs.card_edit_base_revs as Readonly<Record<string, number>> : undefined;
        const roots = new Set([frame.encounter_id.toUpperCase(), ...Object.keys(cardRevisions ?? {}), ...Object.keys(frame.computed), ...Object.keys(frame.computed_delta ?? {}),
            ...Object.keys(frame.member_revs), ...frame.new_entities.filter(entity => !entity.pending).map(entity => entity.entity_id)]);
        const covered = new Set([...roots, ...frame.rows.filter(row => row.table === 'action_bars')
            .map(row => String(row.row.owner_id ?? '').toUpperCase()), ...frame.rows.filter(row => row.table === 'inventory').flatMap(row =>
            [row.row_id.toUpperCase(), String(readInventoryField({ inventory: row.row }, 'possess_id') ?? '').toUpperCase()]),
            ...(frame.new_inventory ?? []).map(birth => birth.possess_id.toUpperCase())]);
        for (const envelope of held.values()) {
            if (savedEnvelope && envelope !== savedEnvelope) continue;
            const id = idOf(envelope.entity_id);
            if (actionBarsOnlyFrame(frame) && layoutRevisions?.[id] !== undefined && layoutRevisions[id]! <= envelope.base_rev) continue;
            if (!covered.has(id) && !(actionBarsOnlyFrame(frame) && holdsSavedActionBars(envelope.base, frame))) continue;
            // The installer owns adopted subtrees; classifiers only borrow the decode.
            const input = frame;
            const oldKey = memoKey(envelope), previous = compiled.get(oldKey)?.result;
            const output = previous?.computed ?? envelope.cache?.computed;
            if (output === undefined) { recoverApplied(id, envelope.entity); continue; }
            const beforeConditions = envelope.base;
            const installed = applyAppliedFrame(input, output, false, true, envelope.entity_id, false, envelope.metadata as unknown as import('./from-base-encounter-terrain').EncounterTerrainMetadata);
            for (const owner of installed.inventoryRecover) {
                if (envelope.entity === 'inventory') recoverApplied(owner, 'inventory'); else hooks.recoverInventory?.(owner);
            }
            for (const member of installed.recover) recoverApplied(member, 'creature', input.member_revs[member] ?? 0);
            if (installed.recover.includes(id)) continue;
            // Merge delivered definitions before extending saved order witnesses.
            const savedSource = savedInputs ? mergeInventoryMetadata(envelope.base, savedInputs) : envelope.base;
            const saved = applyAppliedFrame(input, savedSource, true, false, undefined, true);
            for (const member of saved.recover) recoverApplied(member, 'creature', input.member_revs[member] ?? 0);
            const base = saved.value as BaseEnvelope['base'];
            if (installed.value === output && base === envelope.base && savedRevision === undefined && !frame.new_entities.length) continue;
            // A new/removed status also writes its caster's facet without moving
            // that caster's EC revision. Unchanged miss targets do not consume a revision.
            const memberWritten = (member: Record<string, unknown>): boolean => {
                const memberId = String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
                const rev = input.member_revs[memberId];
                if (Number.isSafeInteger(rev) && rev! > Number(encounterCreatureField(member.encounter_creatures, 'movement_rev') ?? 0)
                    && input.rows.some(row => row.table === 'encounter_creatures' && row.row_id.toUpperCase() === memberId)) return true;
                if (!input.rows.some(row => row.table === 'statuses') && !input.deletes.some(row => row.table === 'statuses')) return false;
                const statuses = new Set((['target', 'caster'] as const).flatMap(side => Object.values(readCreatureStatuses(member, side))).filter(fromBaseRecord)
                    .map(row => String(readItemField(row, 'status_id') ?? '').toUpperCase()));
                return input.deletes.some(row => row.table === 'statuses' && statuses.has(row.row_id.toUpperCase()))
                    || input.rows.some(row => row.table === 'statuses' && !statuses.has(row.row_id.toUpperCase())
                        && ['status_target_id', 'status_caster_id'].some(key => String(readItemField(row.row, key) ?? '').toUpperCase() === memberId));
            };
            const committedRevision = savedRevision ?? cardRevisions?.[id];
            const committedDependencies = savedDependencies ?? cardRevisions;
            if (committedRevision !== undefined) envelope.base_rev = cardRevisions
                ? Math.max(envelope.base_rev, committedRevision) : committedRevision;
            else if (memberWritten(envelope.base) || envelope.entity === 'encounter' && Array.isArray(envelope.base.encounter_creatures_arr)
                && envelope.base.encounter_creatures_arr.some(member => member && typeof member === 'object' && memberWritten(member))) envelope.base_rev++;
            envelope.base = { ...base, base_rev: envelope.base_rev };
            if (committedRevision !== undefined) {
                if (fromBaseRecord(envelope.base.dependency_revs)) {
                    const dependencies = { ...envelope.base.dependency_revs };
                    for (const [dependency, rev] of Object.entries(committedDependencies ?? {})) if (dependency in dependencies) dependencies[dependency] = rev;
                    dependencies[id] = committedRevision;
                    envelope.base.dependency_revs = dependencies;
                }
                if (typeof envelope.base.catalog_ref === 'string') envelope.base.catalog_ref = envelope.base.catalog_ref.replace(/:\d+$/, `:${committedRevision}`);
                const hosts = [envelope.base, ...(Array.isArray(envelope.base.encounter_creatures_arr) ? envelope.base.encounter_creatures_arr : [])].filter(fromBaseRecord);
                // A saved resource row also covers its encounter-owned member, without a standalone facet.
                if (input.operation_id.startsWith('turn-resource:')) for (const row of input.rows) {
                    const owner = idOf(row.row_id), revision = committedDependencies?.[owner];
                    const member = row.table === 'encounter_creatures' && hosts.find(host => host !== envelope.base
                        && idOf(String(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id') ?? '')) === owner);
                    if (member && revision === Number(member.base_rev ?? 0) + 1) member.base_rev = revision;
                }
                for (const host of hosts) {
                    const owner = String(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
                    if (host !== envelope.base && committedDependencies?.[owner] !== undefined
                        && (cardRevisions?.[owner] !== undefined
                            || input.operation_id.startsWith('saved-payload:') && committedDependencies[owner] === Number(host.base_rev ?? 0) + 1
                            || input.new_inventory?.some(birth => birth.possess_id.toUpperCase() === owner)
                            || input.rows.some(row => row.table === 'inventory' && String(readInventoryField({ inventory: row.row }, 'possess_id') ?? '').toUpperCase() === owner)))
                        host.base_rev = committedDependencies[owner];
                    for (const node of Array.isArray(host.inventory) ? host.inventory.filter(fromBaseRecord) : []) {
                        const rowId = String(readInventoryField(node, 'inventory_id') ?? '').toUpperCase();
                        if ('base_rev' in node && input.rows.some(row => row.row_id.toUpperCase() === rowId) && committedDependencies?.[rowId] !== undefined)
                            node.base_rev = committedDependencies[rowId];
                    }
                }
            }
            evict(oldKey);
            const key = memoKey(envelope);
            compiled.set(key, { entity: envelope.entity, id, result: { computed: installed.value,
                stagesRun: previous?.stagesRun ?? [], stagesMissing: [], stageDiagnostics: previous?.stageDiagnostics } });
            envelope.cache = null;
            winners.set(baseEntityKey(id, envelope.entity), { key, base: envelope.base });
            deliver(baseEntityKey(id, envelope.entity), envelope.base_rev);
            hooks.discard?.(envelope);
            if (hooks.installPaint) hooks.installPaint(envelope, wire, frame);
            else hooks.paint(envelope, installed.value);
            if (frame.rows.some(row => ['encounter_creatures', 'inventory', 'statuses', 'encounters', 'campaigns'].includes(row.table))
                || frame.deletes.some(row => ['encounter_creatures', 'inventory', 'statuses'].includes(row.table))
                || frame.new_entities.length || Object.keys(frame.computed).length || Object.keys(frame.computed_delta ?? {}).length)
                refreshSceneConditions(envelope, beforeConditions, output);
        }
        // Completed births adopt the actor's first compile. GM raw births paint
        // immediately, then share one worker batch on this page.
        const pending: AppliedNewEntity[] = [];
        for (const newborn of frame.new_entities) if ((hooks.installNewEntities ?? [...held.values()].some(held => held.entity !== 'inventory')) && !get(newborn.entity_id, newborn.entity)) {
            const envelope: BaseEnvelope = { ...context, entity: newborn.entity, entity_id: newborn.entity_id, base_rev: newborn.base_rev,
                engine, base: newborn.base, cache: null };
            held.set(baseEntityKey(newborn.entity_id, newborn.entity), envelope);
            const key = memoKey(envelope);
            compiled.set(key, { entity: newborn.entity, id: idOf(newborn.entity_id), result: initialBirthResult(newborn) });
            winners.set(baseEntityKey(newborn.entity_id, newborn.entity), { key, base: envelope.base });
            deliver(baseEntityKey(newborn.entity_id, newborn.entity), newborn.base_rev);
            if (hooks.installPaint) hooks.installPaint(envelope, wire, frame); else hooks.paint(envelope, newborn.member);
            if (newborn.pending) pending.push(newborn);
        }
        if (pending.length && context) {
            const inputs = new Map(pending.map(entity => {
                const { base, base_rev, metadata, engine, catalog_version } = get(entity.entity_id, entity.entity)!;
                return [entity.entity_id, { base, base_rev, metadata, engine, catalog_version }] as const;
            }));
            const accepted = frame;
            const birthContext = context;
            // Start after the synchronous placeholder paint, outside the save's
            // critical path. Replays cannot schedule a second compile.
            const flight = Promise.resolve().then(async () => {
                const completed = hooks.compileNewEntities ? await hooks.compileNewEntities(pending, birthContext, (shared, metadata) => {
                    if (get(birthContext.entity_id, birthContext.entity) !== birthContext) return;
                    for (const [id, initial] of inputs) {
                        Object.assign(initial.base, shared); inputs.set(id, { ...initial, metadata });
                    }
                    // Hydration changes the metadata identity in the memo key.
                    // Re-key the same compiled value, without repainting or compiling held members.
                    for (const envelope of [birthContext, ...pending.flatMap(entity => {
                        const held = get(entity.entity_id, entity.entity); return held ? [held] : [];
                    })]) {
                        const oldKey = memoKey(envelope), previous = compiled.get(oldKey);
                        Object.assign(envelope.base, shared);
                        envelope.metadata = metadata;
                        const key = memoKey(envelope);
                        if (key !== oldKey) { evict(oldKey); if (previous) compiled.set(key, previous); }
                        winners.set(baseEntityKey(envelope.entity_id, envelope.entity), { key, base: envelope.base });
                    }
                })
                    : await Promise.all(pending.map(async ({ pending: _pending, ...entity }) => {
                        const result = await hooks.compile(get(entity.entity_id, entity.entity)!);
                        if (result.stagesMissing.length || !fromBaseRecord(result.computed)) throw new Error(`spawn:compile:${result.stagesMissing.join(',')}`);
                        return { ...entity, member: result.computed };
                    }));
                if (get(birthContext.entity_id, birthContext.entity) !== birthContext) return;
                const current = completed.flatMap(entity => {
                    if (!Array.isArray(context.base.encounter_creatures_arr) || !context.base.encounter_creatures_arr.some(member => fromBaseRecord(member)
                        && String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id')).toUpperCase() === entity.entity_id)) return [];
                    const birth = completePendingBirth(entity, birthInputs.get(entity.entity_id)!, inputs.get(entity.entity_id)!, get(entity.entity_id, entity.entity));
                    if (!birth) recoverApplied(entity.entity_id, entity.entity);
                    return birth ? [birth] : [];
                });
                if (!current.length) return;
                // Inputs are already held locally. Avoid serializing their
                // hydrated catalog again for the completion paint.
                const entities = current.map(birth => birth.entity);
                const revisions = current.flatMap(birth => birth.revision ? [birth.revision] : []);
                install(JSON.stringify({ ...accepted, operation_id: `${accepted.operation_id}:compiled`, rows: revisions,
                    member_revs: { ...accepted.member_revs, ...Object.fromEntries(revisions.map(row => [row.row_id, Number(encounterCreatureField(row.row, 'movement_rev') ?? 0)])) },
                    inputs: undefined, computed: {}, computed_delta: undefined, new_entities: entities }));
            }).catch(error => {
                for (const entity of pending) refuse(entity.entity_id, [error instanceof Error ? error.message : 'spawn:compile'], entity.entity);
            }).finally(() => { for (const entity of pending) if (birthFlights.get(entity.entity_id) === flight) birthFlights.delete(entity.entity_id); });
            for (const entity of pending) birthFlights.set(entity.entity_id, flight);
        }
        if (context) bankCompletedBirths(frame.new_entities.filter(entity => !entity.pending), context);
    }
    async function apply(frame: BasePatchFrame, actor: boolean | ReadonlySet<string> = false, decoded?: AppliedFrameResult): Promise<void> {
        let applied: (() => void) | undefined;
        try {
            if (frame.applied) {
                decoded ??= parsedAppliedFrame(frame.applied);
                applied = hooks.observeApplied?.(frame.applied, held.values(), decoded);
            }
        }
        catch { /* Invalid wire retains the installer's existing recovery path. */ }
        try {
        if (paused) return;
        if (frame.applied) {
            const layout = decoded && !('cache_ref' in decoded) && actionBarsOnlyFrame(decoded, true) ? decoded : undefined;
            try { if (!layout || layout.rows.length || layout.deletes.length) install(frame.applied, undefined, undefined, undefined, decoded, frame.base_revs); }
            catch { if (frame.encounter_id) recoverApplied(frame.encounter_id, 'encounter'); }
            if (frame.base_revs && layout) acknowledgeSavedRevisions(frame.base_revs);
            return;
        }
        const isWriter = (id: string): boolean => actor === true || (actor !== false && actor.has(idOf(id)));
        // Pub/sub is shared across deployments. A foreign engine requests our own base JSON;
        // only an HTTP response from this page's server can activate the version fence.
        if (frame.engine && frame.engine !== engine) {
            const ids = new Set([...frame.ops.map(op => idOf(op.entity_id)), ...(frame.resync ?? []).map(idOf)]);
            await Promise.all([...ids].flatMap(id => forId(id).map(envelope => resync(id, envelope.entity, 0, isWriter(id)))));
            return;
        }
        // Snapshot before any group changes a held source. Former-holder tombstones also
        // distinguish movement from a fresh add when that source facet is not loaded.
        const retiringMembers = new Set(frame.ops.filter(op => op.table === 'encounter_creatures' && op.op === 'delete').map(op => idOf(op.row_id)));
        const retiringInventory = new Map<string, Record<string, unknown>>();
        if (retiringMembers.size) for (const envelope of held.values()) {
            const members = [envelope.base, ...Array.isArray(envelope.base.encounter_creatures_arr)
                ? envelope.base.encounter_creatures_arr.filter(fromBaseRecord) : []];
            for (const member of members) if (retiringMembers.has(idOf(String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? ''))))
                for (const row of readInventoryRows(member, 'inventory')) retiringInventory.set(idOf(String(readInventoryField(row, 'inventory_id') ?? '')), row);
        }
        const priorInventoryIds = Object.assign(new Set(frame.ops.some(op => op.table === 'inventory' && op.op === 'upsert')
            ? [...held.values()].flatMap(envelope => baseInventoryIds(envelope.base)) : []), { retiringInventory: [...retiringInventory.values()] });
        for (const op of frame.ops) if (op.table === 'inventory' && op.op === 'delete') priorInventoryIds.add(idOf(op.row_id));
        // Only a creature's own tombstone closes its facet. An encounter's
        // membership tombstone may instead be a transfer to another encounter.
        // Retire before resync: the relay may request both in the same frame.
        const destroyedMembers = new Set<string>();
        for (const op of frame.ops) if (op.table === 'encounter_creatures' && op.op === 'delete'
            && idOf(op.row_id) === idOf(op.entity_id)) {
            destroyedMembers.add(idOf(op.row_id));
            retireFacet(op.row_id, 'creature');
            retireFacet(op.row_id, 'inventory');
        }
        const groups = new Map<string, typeof frame.ops>();
        for (const op of frame.ops) {
            const id = idOf(op.entity_id), group = groups.get(id) ?? [];
            group.push(op); groups.set(id, group);
        }
        const savedMoves = new Map<BaseEnvelope, { base: BaseEnvelope['base']; revision: number;
            plan: { frame: AppliedFrame; inputs: Record<string, unknown> } }>();
        // Prepare every facet before any installs: the room and the independent
        // creature facet borrow the same source closure for this one delivery.
        await Promise.all([...groups].flatMap(([, ops]) => forId(ops[0]!.entity_id).map(async envelope => {
            const revision = Math.max(...ops.map(op => op.base_rev)), before = envelope.base;
            if (revision < envelope.base_rev || revision > envelope.base_rev + 1 || !ops.every(op => op.base_rev === revision)) return;
            const computed = compiled.get(memoKey(envelope))?.result.computed ?? envelope.cache?.computed;
            if (computed === undefined) return;
            const move = await savedInventoryMoveFrame(envelope, ops, computed, hooks.inventoryMoveContext?.(envelope));
            if (move && envelope.base === before && revision >= envelope.base_rev && revision <= envelope.base_rev + 1)
                savedMoves.set(envelope, { base: before, revision, plan: move });
        })));
        const installSavedMove = (envelope: BaseEnvelope, ops: readonly BasePatchOp[]): boolean => {
            const saved = savedMoves.get(envelope);
            if (!saved || envelope.base !== saved.base || saved.revision < envelope.base_rev || saved.revision > envelope.base_rev + 1) return false;
            const { revision, plan: move } = saved;
            install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), move.frame, undefined, move.inputs);
            savedMoveReceipts.set(envelope, { revision, ops });
            return true;
        };
        await Promise.all([...new Set((frame.resync ?? []).map(idOf))].flatMap(id => forId(id).map(envelope => {
            const ops = groups.get(id), revision = ops && Math.max(...ops.map(op => op.base_rev));
            // A native Destroy's complete membership tombstone covers this room's
            // next revision. Use the shared roster diff before its recovery hint.
            // Gaps, missing output and mixed/uncovered changes keep their read lane.
            if (envelope.entity === 'encounter' && ops?.length && revision !== undefined
                && (revision === envelope.base_rev || revision === envelope.base_rev + 1)
                && (states.get(baseEntityKey(id, envelope.entity))?.required ?? revision) <= revision
                && ops.every(op => op.base_rev === revision && op.table === 'encounter_creatures' && op.op === 'delete'
                    && destroyedMembers.has(idOf(op.row_id)) && !op.inputs && !op.catalog_inputs)) {
                const output = compiled.get(memoKey(envelope))?.result.computed ?? envelope.cache?.computed;
                if (output !== undefined) {
                    if (revision === envelope.base_rev && Array.isArray(envelope.base.encounter_creatures_arr)
                        && envelope.base.encounter_creatures_arr.every(member => !fromBaseRecord(member)
                            || !ops.some(op => idOf(op.row_id) === idOf(String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? ''))))) return;
                    if (revision === envelope.base_rev + 1) {
                        const removal = savedCreatureRelocationFrame(envelope, ops, [], output);
                        if (removal) {
                            install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), removal);
                            if (envelope.base_rev === revision) return;
                        }
                    }
                }
            }
            return (async () => {
                if (ops?.length && installSavedMove(envelope, ops)) return;
                // A replay whose saved rows are already covered cannot turn its
                // old recovery hint into another reload of the same revision.
                const moved = savedMoveReceipts.get(envelope);
                if (ops?.length && moved && revision === moved.revision && revision! <= envelope.base_rev
                    && await sameTree(moved.ops, ops)) return;
                await resync(id, envelope.entity, 0, isWriter(id));
            })();
        })));
        // Fetch only newly arriving bodies, once for the frame. A source tombstone and a position update need no read.
        const arrivals = new Set<string>(), potential = new Map<BaseEnvelope, string[]>();
        const relocationEnvelopes = new Set<BaseEnvelope>();
        if (hooks.loadMembers) for (const [id, ops] of groups) for (const envelope of forId(id)) {
            const revision = Math.max(...ops.map(op => op.base_rev));
            const incoming = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                ? relocationArrivalIds(envelope, partyPlayerOps(ops)?.remaining ?? ops) : null;
            if (incoming) potential.set(envelope, incoming);
        }
        const checked = Promise.all([...potential].map(async ([envelope, incoming]) => ({ envelope, incoming,
            unchanged: await relocationDefinitionsUnchanged(envelope.base, groups.get(idOf(envelope.entity_id))!) })));
        const readings = new Map<Promise<RelocationMemberBatch>, string[]>(), unread = new Map<string, string>();
        // Reserve identities before yielding for definition comparisons: a concurrent PC echo joins the enclosing roster batch.
        for (const id of new Set([...potential.values()].flat())) {
            const op = frame.ops.find(op => op.table === 'encounter_creatures' && op.op === 'upsert' && idOf(op.row_id) === id)!;
            const key = JSON.stringify([id, encounterCreatureField(op.cols, 'encounter_id'), encounterCreatureField(op.cols, 'movement_rev'),
                encounterCreatureField(op.cols, 'movement_last_operation_id')]);
            const reading = relocationFlights.get(key);
            if (reading) { const ids = readings.get(reading) ?? []; ids.push(id); readings.set(reading, ids); }
            else unread.set(id, key);
        }
        if (unread.size) {
            const reading = checked.then(checked => {
                const ids = [...new Set(checked.filter(row => row.unchanged).flatMap(row => row.incoming))].filter(id => unread.has(id));
                return ids.length ? hooks.loadMembers!(ids, frame.ops) : { members: [], failures: [] };
            });
            for (const key of unread.values()) relocationFlights.set(key, reading);
            readings.set(reading, [...unread.keys()]);
            const retire = (): void => { for (const key of unread.values()) if (relocationFlights.get(key) === reading) relocationFlights.delete(key); };
            void reading.then(retire, retire);
        }
        for (const row of await checked) if (row.unchanged) {
            relocationEnvelopes.add(row.envelope); row.incoming.forEach(id => arrivals.add(id));
        }
        const batches = await Promise.all([...readings].map(async ([reading, ids]) => {
            try { return await reading; }
            catch (error) { return { members: [], failures: ids.map(id => ({ id,
                reason: error instanceof Error ? error.message : 'relocation-member-batch:read' })) }; }
        }));
        const members = batches.flatMap(batch => batch.members).filter(member => arrivals.has(idOf(member.entity_id)));
        const failures: RelocationMemberFailure[] = [...arrivals].filter(id => !members.some(member => idOf(member.entity_id) === id)).map(id => ({ id,
            reason: batches.flatMap(batch => batch.failures).find(failure => idOf(failure.id) === id)?.reason ?? 'relocation-member-batch:member-unavailable' }));
        const unavailable = new Set(failures.map(failure => idOf(failure.id)));
        if (failures.length) (hooks.warn ?? console.warn)('Relocation member recovery: ' + failures.map(failure => {
            const op = frame.ops.find(op => op.table === 'encounter_creatures' && idOf(op.row_id) === idOf(failure.id));
            return `${String(encounterCreatureField(op?.cols, 'encounter_creature_name') || 'Creature')}: ${failure.reason}`;
        }).join('; '));
        await Promise.all([...groups].flatMap(([id, ops]) => forId(id).map(async envelope => {
            const key = baseEntityKey(id, envelope.entity);
            const revision = Math.max(...ops.map(op => op.base_rev));
            // A requirement nobody is recovering (its recovery was exhausted) is read again rather than patched beneath;
            // while a recovery runs it owns the requirement and an in-sequence op is applied in place as usual.
            const open = states.get(key)?.required;
            if (open !== undefined && envelope.base_rev < open && !recovering(id, envelope.entity)) { await resync(id, envelope.entity, Math.max(open, revision), isWriter(id)); return; }
            try {
                if (installSavedMove(envelope, ops)) return;
                const computed = compiled.get(memoKey(envelope))?.result.computed ?? envelope.cache?.computed;
                const party = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? savedPartyPlayerFrame(envelope, ops, members, computed, unavailable) : null;
                if (party) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), party);
                    return;
                }
                const grantBase = envelope.base;
                const grants = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? await hooks.savedSkillGrantPatch?.(envelope, ops, computed) : null;
                if (envelope.base !== grantBase) {
                    if (revision > envelope.base_rev) await resync(id, envelope.entity, revision, isWriter(id));
                    return;
                }
                if (grants) {
                    const oldKey = memoKey(envelope), previous = compiled.get(oldKey)?.result;
                    envelope.base = grants.base; envelope.base_rev = revision; envelope.base.base_rev = revision;
                    evict(oldKey);
                    const nextKey = memoKey(envelope);
                    compiled.set(nextKey, { entity: envelope.entity, id, result: { computed: grants.computed,
                        stagesRun: previous?.stagesRun ?? [], stagesMissing: [], stageDiagnostics: previous?.stageDiagnostics } });
                    envelope.cache = null;
                    winners.set(key, { key: nextKey, base: envelope.base }); deliver(key, revision);
                    hooks.discard?.(envelope);
                    hooks.paintSkillGrantPatch?.(envelope, grants);
                    return;
                }
                const chargeBase = envelope.base;
                const charges = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? await savedChargeFrame(envelope, ops, computed) : null;
                if (charges && envelope.base === chargeBase && revision === envelope.base_rev + 1) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), charges.frame, undefined, charges.inputs);
                    return;
                }
                const turnResources = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? savedTurnResourceFrame(envelope, ops) : null;
                if (turnResources) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), turnResources);
                    return;
                }
                const birthBase = envelope.base;
                const inventoryBirth = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? await savedInventoryBirthFrame({ ...envelope }, ops, computed, priorInventoryIds) : null;
                if (inventoryBirth && envelope.base === birthBase && revision === envelope.base_rev + 1) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), inventoryBirth.frame, undefined, inventoryBirth.inputs);
                    return;
                }
                if (relocationEnvelopes.has(envelope) && revision === envelope.base_rev + 1 && computed !== undefined) {
                    const relocation = savedCreatureRelocationFrame(envelope, ops, members, computed, unavailable);
                    if (relocation) {
                        install(JSON.stringify(relocation), envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})));
                        for (const arrival of relocation.new_entities) {
                            const member = get(arrival.entity_id, 'creature');
                            // A compact arrival (destination held) omits its context: it names the frame's room.
                            if (!member || member.base_rev > arrival.base_rev || member.base_rev === arrival.base_rev
                                && encounterField(member.base.encounter_context, 'encounter_id') === (encounterField(arrival.base.encounter_context, 'encounter_id') ?? relocation.encounter_id)) continue;
                            install(JSON.stringify({ ...relocation, operation_id: `${relocation.operation_id}:member:${arrival.entity_id}`,
                                new_entities: [arrival], rows: relocation.rows.filter(row => idOf(row.row_id) === idOf(arrival.entity_id)),
                                deletes: [], member_revs: { [arrival.entity_id]: relocation.member_revs[arrival.entity_id] } }), member, arrival.base_rev);
                        }
                        return;
                    }
                }
                const vitalityRows = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? savedInventoryVitalityRows(envelope.base, ops) : null;
                if (vitalityRows) {
                    const applied: AppliedFrame = { kind: 'applied', operation_id: `inventory-vitality:${key}:${revision}`,
                        encounter_id: id, actor_id: '', rev: 0, member_revs: {}, rows: vitalityRows, computed: {}, new_entities: [], deletes: [] };
                    install(JSON.stringify(applied), envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})));
                    return;
                }
                const positionBase = envelope.base;
                const position = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? await savedFloorPositionPatch(positionBase, ops, computed) : null;
                if (position && envelope.base === positionBase && revision === envelope.base_rev + 1) {
                    const applied: AppliedFrame = { kind: 'applied', operation_id: `floor-position:${key}:${revision}`,
                        encounter_id: id, actor_id: '', rev: 0, member_revs: {}, rows: position.rows, computed: {}, new_entities: [], deletes: [] };
                    install(JSON.stringify(applied), envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), undefined, undefined, position.inputs);
                    return;
                }
                const payloadBase = envelope.base;
                const payload = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? await savedPayloadMemberFrame(envelope, ops, computed) : null;
                if (payload && envelope.base === payloadBase && revision === envelope.base_rev + 1) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), payload);
                    return;
                }
                const links = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? savedInventoryLinkFrame(envelope, ops) : null;
                if (links) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), links);
                    return;
                }
                const floorBase = envelope.base;
                const floor = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined
                    ? await savedFloorInventoryFrame({ ...envelope }, ops, computed, hooks.compile, priorInventoryIds) : null;
                if (floor && envelope.base === floorBase && revision === envelope.base_rev + 1) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), floor.frame, undefined, floor.inputs);
                    return;
                }
                // Mount eligibility is decided synchronously: an ordinary in-sequence patch never
                // yields here, and the resync below is reachable only for a saved trap mount.
                const mount = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision) && computed !== undefined
                    ? floorEnchantmentMount(envelope, ops, computed) : null;
                if (mount) {
                    let before = envelope.base;
                    let mounted = await compileFloorEnchantmentMount(mount, hooks.compile);
                    if (before !== envelope.base) {
                        before = envelope.base;
                        const newer = compiled.get(memoKey(envelope))?.result.computed ?? envelope.cache?.computed;
                        // The walk may have consumed this enclosing revision. Patch a
                        // scoped input at the op's revision while retaining the newer held rows.
                        const retry = newer === undefined ? null : floorEnchantmentMount({ ...envelope, base_rev: revision - 1 }, ops, newer);
                        mounted = retry ? await compileFloorEnchantmentMount(retry, hooks.compile) : null;
                    }
                    if (mounted && before === envelope.base) {
                        install(JSON.stringify(mounted), envelope, Math.max(revision, envelope.base_rev), Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})));
                        return;
                    }
                    // A second concurrent install, or a failed retry after a walk,
                    // must recover the saved mount even when the ordinary patch is a duplicate.
                    if (revision <= envelope.base_rev) { await resync(id, envelope.entity, revision, isWriter(id)); return; }
                }
                const shapeRows = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? savedEncounterShapeRows(envelope.base, ops) : null;
                if (shapeRows) {
                    const applied: AppliedFrame = { kind: 'applied', operation_id: `encounter-shape:${key}:${revision}`,
                        encounter_id: id, actor_id: '', rev: 0, member_revs: {}, rows: shapeRows, computed: {}, new_entities: [], deletes: [] };
                    install(JSON.stringify(applied), envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})));
                    return;
                }
                const equipmentBase = envelope.base;
                const equipment = revision === envelope.base_rev + 1 && ops.every(op => op.base_rev === revision)
                    && computed !== undefined ? await savedEquipmentFrame({ ...envelope }, ops, computed) : null;
                if (equipment && envelope.base === equipmentBase && revision === envelope.base_rev + 1) {
                    install('', envelope, revision, Object.assign({}, ...ops.map(op => op.dependency_revs ?? {})), equipment.frame, undefined, equipment.inputs);
                    return;
                }
                const outcome = applyBasePatch(envelope, ops, priorInventoryIds);
                if (outcome === 'gap' || outcome === 'resync') await resync(id, envelope.entity, revision, isWriter(id));
                else if (outcome === 'applied') { deliver(key, envelope.base_rev); await compile(envelope, isWriter(id)); }
            } catch (error) {
                refuse(id, [error instanceof Error ? error.message : 'base-patch-apply'], envelope.entity);
            }
        })));
        // Failed arrivals recover individually after successful rows and source tombstones have painted. Never reload the room.
        const dependencyRevisions = Object.assign({}, ...frame.ops.map(op => op.dependency_revs ?? {})) as Record<string, number>;
        await Promise.all(failures.map(async failure => {
            const id = idOf(failure.id), op = frame.ops.find(op => op.table === 'encounter_creatures' && op.op === 'upsert' && idOf(op.row_id) === id);
            if (!op) return;
            const required = dependencyRevisions[id] ?? 0;
            const movement = Number(encounterCreatureField(op.cols, 'movement_rev') ?? 0);
            const memberKey = baseEntityKey(id, 'creature');
            appliedMinimum.set(memberKey, Math.max(appliedMinimum.get(memberKey) ?? 0, movement));
            await resync(id, 'creature', required, isWriter(id));
            const recovered = get(id, 'creature');
            const output = recovered && (compiled.get(memoKey(recovered))?.result.computed ?? recovered.cache?.computed);
            if (!recovered || !fromBaseRecord(output) || !isCurrent(recovered)) return;
            const destination = String(encounterCreatureField(op.cols, 'encounter_id') ?? '').toUpperCase();
            if (recovered.base_rev < required || Number(encounterCreatureField(recovered.base.encounter_creatures, 'movement_rev') ?? 0) < movement
                || String(encounterCreatureField(recovered.base.encounter_creatures, 'encounter_id') ?? '').toUpperCase() !== destination
                || String(encounterField(recovered.base.encounter_context, 'encounter_id') ?? '').toUpperCase() !== destination) {
                refuse(id, ['relocation-member-recovery-stale'], 'creature'); return;
            }
            const arrival: AppliedNewEntity = { entity: 'creature', entity_id: id, base_rev: recovered.base_rev, base: recovered.base, member: output };
            for (const envelope of relocationEnvelopes) {
                if (get(envelope.entity_id, envelope.entity) !== envelope
                    || envelope.entity !== 'encounter' || idOf(String(encounterField(envelope.base, 'encounter_id'))) !== idOf(String(encounterCreatureField(op.cols, 'encounter_id')))) continue;
                const computed = compiled.get(memoKey(envelope))?.result.computed;
                const relocation = savedCreatureRelocationFrame(envelope, [op], [arrival], computed);
                if (!relocation) continue;
                relocation.operation_id += `:recovered:${id}`;
                install(JSON.stringify(relocation), envelope, envelope.base_rev, dependencyRevisions);
            }
        }));
        } finally {
            await Promise.all(conditionFlights.values());
            applied?.();
        }
    }
    /** The page's own save answered: persist eligible touched facets as their writer; superseded revisions need no work. The HELD revision is already painted — the save's
     *  echo is still on its way, or its compile is in flight — so this never repaints it: a repaint of the pre-save revision put back
     *  what the page showed before the save (fbspeedlag2: a preferred-speed switch reverted 2 ms after its 200, until the echo's
     *  compile landed ~8 s later). A facet a recovery is reading takes the writer role on that read instead. */
    async function persistTouched(ids: readonly string[]): Promise<void> {
        if (paused) return;
        for (const id of ids) for (const envelope of forId(id)) {
            if (hooks.canWriteBack?.(envelope) === false) continue;
            if (pending.has(baseEntityKey(envelope.entity_id, envelope.entity))) { await resync(envelope.entity_id, envelope.entity, 0, true); continue; }
            await compile(envelope, true, false);
        }
    }
    /** Join current recovery or compilation without starting work; follow a superseding revision. */
    function settled(id: string, entity?: string): Promise<void> | undefined {
        if (paused) return;
        const reads = keysFor(id, entity).flatMap(key => {
            const read = pending.get(key); return read ? [read.promise] : [];
        });
        const compiles = forId(id, entity).flatMap(envelope => {
            const flight = flights.get(memoKey(envelope)); return flight ? [flight.promise] : [];
        });
        const birth = birthFlights.get(idOf(id));
        if (birth) compiles.push(birth);
        const conditions = conditionFlights.get(idOf(id));
        if (conditions) compiles.push(conditions);
        return reads.length || compiles.length ? Promise.all([...reads, ...compiles]).then(() => settled(id, entity)) : undefined;
    }
    /** Same-origin embedded surfaces may reuse a parent's exact computed
     * revision while keeping their own viewer grants and surface lifecycle. */
    async function cached(envelope: BaseEnvelope): Promise<BaseEnvelope['cache']> {
        await settled(envelope.entity_id, envelope.entity);
        const current = get(envelope.entity_id, envelope.entity);
        if (!current || paused || baseEntityKind(current.entity) !== baseEntityKind(envelope.entity) || current.engine !== envelope.engine
            || current.base_rev !== envelope.base_rev || !current.catalog_version || current.catalog_version !== envelope.catalog_version) return;
        // A fresh read may carry new dependency/status context at the same
        // revision. Hydration adds catalog data, but must not hide saved-row drift.
        const pinned = { ...current }, sectionRevision = sectionRevisions.get(current.base);
        const saved = (value: BaseEnvelope) => Object.fromEntries(Object.entries(hooks.savedBase?.(value) ?? value.base)
            .filter(([name]) => name !== 'catalog'));
        if ((envelope !== current && !await sameTree(saved(envelope), saved(current))) || !isCurrent(pinned)
            || sectionRevision !== sectionRevisions.get(current.base)) return;
        const key = memoKey(current), previous = compiled.get(key)?.result;
        const cache = current.cache;
        const acceptedCache = cache && won(current, key) && cache.base_rev === current.base_rev && cache.engine === engine
            && cache.facet === (current.entity === 'inventory' ? 'inventory' : 'entity')
            && (cache.catalog_version ?? null) === current.catalog_version;
        const computed = previous?.computed ?? (acceptedCache ? cache.computed : undefined);
        if (computed === undefined || previous?.stagesMissing.length) return;
        const copy = await copyComputed(computed);
        if (!isCurrent(pinned) || sectionRevision !== sectionRevisions.get(current.base)) return;
        return { facet: pinned.entity === 'inventory' ? 'inventory' : 'entity', entity: pinned.entity, base_rev: pinned.base_rev,
            engine, catalog_version: pinned.catalog_version, computed: copy,
            stage_diagnostics: [...previous?.stageDiagnostics ?? cache?.stage_diagnostics ?? []] };
    }
    function* memberEnvelopes(id: string): Generator<BaseEnvelope> {
        const direct = get(id, 'creature');
        if (direct) yield direct;
        const target = idOf(id);
        for (const envelope of held.values()) if (envelope.entity === 'encounter'
            && Array.isArray(envelope.base.encounter_creatures_arr)
            && envelope.base.encounter_creatures_arr.some(row => fromBaseRecord(row) && idOf(String(row.entity_id)) === target)) yield envelope;
    }
    const sectionRevisions = new WeakMap<BaseEnvelope['base'], number>();
    function contextualMemberBase(base: BaseEnvelope['base']): BaseEnvelope['base'] {
        const encounterId = String(encounterCreatureField(base.encounter_creatures, 'encounter_id') ?? '');
        const enclosing = get(encounterId, 'encounter');
        if (!enclosing || !memberReadsSceneConditions(base, fromBaseRecord(enclosing.base.catalog) ? enclosing.base.catalog : {})) return base;
        const output = compiled.get(memoKey(enclosing))?.result.computed ?? enclosing.cache?.computed;
        return fromBaseRecord(output) ? encounterMemberInput({ ...enclosing.base, condition_roster: output.encounter_creatures_arr,
            condition_inventory: output.encounter_inventories_arr }, base) : base;
    }
    /** Borrow the exact raw/member pair for a section compiler. No copying or recovery. */
    function memberContext(id: string): { envelope: BaseEnvelope; base: BaseEnvelope['base']; computed: Record<string, unknown>; sectionRevision: number } | undefined {
        const target = idOf(id);
        for (const envelope of memberEnvelopes(id)) {
            const output = compiled.get(memoKey(envelope))?.result.computed ?? envelope.cache?.computed;
            if (!fromBaseRecord(output)) continue;
            const sectionRevision = sectionRevisions.get(envelope.base) ?? 0;
            if (idOf(envelope.entity_id) === target && baseEntityKind(envelope.entity) === 'creature') return { envelope, base: contextualMemberBase(envelope.base), computed: output, sectionRevision };
            if (envelope.entity !== 'encounter' || !Array.isArray(envelope.base.encounter_creatures_arr) || !Array.isArray(output.encounter_creatures_arr)) continue;
            const base = envelope.base.encounter_creatures_arr.find(row => fromBaseRecord(row) && idOf(String(row.entity_id)) === target);
            const computed = output.encounter_creatures_arr.find(row => fromBaseRecord(row)
                && idOf(String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'))) === target);
            if (fromBaseRecord(base) && fromBaseRecord(computed)) {
                if (!computed.catalog) computed.catalog = output.catalog;
                return { envelope, base: encounterMemberInput({ ...envelope.base, condition_roster: output.encounter_creatures_arr }, base), computed, sectionRevision };
            }
        }
    }
    /** Bank a decided section delta in every held facet. Replace only the changed member's slots and catalog entries.
     * The caller owns the narrow UI notification; this never calls paint, compile, write-back or load. */
    function patchMemberSections(id: string, patch: (base: BaseEnvelope['base'], computed: Record<string, unknown>) => {
        base: BaseEnvelope['base']; computed: Record<string, unknown> }, catalog?: Record<string, unknown>): void {
        if (paused) return;
        const target = idOf(id);
        for (const envelope of memberEnvelopes(id)) {
            const oldKey = memoKey(envelope), previous = compiled.get(oldKey)?.result;
            const output = previous?.computed ?? envelope.cache?.computed;
            if (!fromBaseRecord(output)) continue;
            let base = envelope.base, computed = output;
            if (baseEntityKind(envelope.entity) === 'creature' && idOf(envelope.entity_id) === target) ({ base, computed } = patch(base, output));
            else if (envelope.entity === 'encounter' && Array.isArray(base.encounter_creatures_arr) && Array.isArray(output.encounter_creatures_arr)) {
                const inputIndex = base.encounter_creatures_arr.findIndex(row => fromBaseRecord(row) && idOf(String(row.entity_id)) === target);
                const outputIndex = output.encounter_creatures_arr.findIndex(row => fromBaseRecord(row)
                    && idOf(String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'))) === target);
                if (inputIndex < 0 || outputIndex < 0) continue;
                const changed = patch(base.encounter_creatures_arr[inputIndex] as BaseEnvelope['base'], output.encounter_creatures_arr[outputIndex] as Record<string, unknown>);
                base.encounter_creatures_arr[inputIndex] = changed.base;
                output.encounter_creatures_arr[outputIndex] = changed.computed;
            } else continue;
            if (catalog && Object.keys(catalog).length)
                computed.catalog = Object.assign(fromBaseRecord(output.catalog) ? output.catalog : {}, catalog);
            // An async section planner must notice edits even though its enclosing base stays held.
            sectionRevisions.set(base, (sectionRevisions.get(envelope.base) ?? 0) + 1);
            envelope.base = base; envelope.cache = null; correctedBases.add(base); evict(oldKey);
            const key = memoKey(envelope);
            compiled.set(key, { entity: envelope.entity, id: idOf(envelope.entity_id), result: {
                ...previous, computed, stagesRun: previous?.stagesRun ?? [], stagesMissing: previous?.stagesMissing ?? [] } });
            winners.set(baseEntityKey(envelope.entity_id, envelope.entity), { key, base });
            hooks.discard?.(envelope);
        }
    }
    /** A committer announces saved bytes. Competing worker output may finish later,
     * but only the first result for this exact current revision paints. */
    function computedReady(envelope: BaseEnvelope): boolean {
        const current = get(envelope.entity_id, envelope.entity);
        const cache = envelope.cache;
        if (!current || correctedBases.has(current.base) || baseEntityKind(current.entity) !== baseEntityKind(envelope.entity) || current.base_rev !== envelope.base_rev
            || envelope.engine !== engine || (envelope.catalog_version ?? null) !== (current.catalog_version ?? null)
            || !cache || cache.base_rev !== current.base_rev || cache.engine !== engine
            || baseEntityKind(cache.entity ?? '') !== baseEntityKind(current.entity)
            || cache.facet !== (current.entity === 'inventory' ? 'inventory' : 'entity')
            || (cache.catalog_version ?? null) !== (current.catalog_version ?? null)) return false;
        const key = memoKey(current);
        if (won(current, key) || compiled.has(key)) return false;
        current.cache = envelope.cache;
        void runCompile({ ...current }, key, () => false);
        hooks.discard?.(current);
        return true;
    }
    return { held, get, adopt, apply, correctSavedRows, acknowledgeSavedRevisions, recoverApplied, checkEngine, resync, persistTouched, settled, cached, computedReady, isCurrent, gap, recover, recovering,
        memberContext, patchMemberSections, stats: () => ({ ...counters }), get paused() { return paused; } };
}
